import React, { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { supabase } from "../supabaseClient";
import {
  SCANNER_APK_OBJECT,
  SCANNER_RELEASES_BUCKET,
  canAccessStaffPortal,
} from "../lib/staffAccess";

const resolveProfileRole = async (user) => {
  if (!user?.id) return "";

  const { data, error } = await supabase
    .from("users")
    .select("role, full_name")
    .eq("id", user.id)
    .maybeSingle();

  if (error) {
    throw new Error("Unable to read your staff profile. Please try again.");
  }

  return {
    role: data?.role || user.user_metadata?.role || "",
    fullName: data?.full_name || user.user_metadata?.full_name || user.email || "Staff",
  };
};

const formatUpdatedAt = (value) => {
  if (!value) return "Upload an APK to scanner-releases to enable download.";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const StaffScanner = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);
  const [apkUpdatedAt, setApkUpdatedAt] = useState("");

  const loadApkMeta = useCallback(async () => {
    const { data, error: listError } = await supabase.storage
      .from(SCANNER_RELEASES_BUCKET)
      .list("", { search: SCANNER_APK_OBJECT, limit: 20 });

    if (listError) {
      setApkUpdatedAt("");
      return;
    }

    const apk = (data || []).find((file) => file.name === SCANNER_APK_OBJECT);
    setApkUpdatedAt(apk?.updated_at || apk?.created_at || "");
  }, []);

  const hydrateSession = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        setProfile(null);
        return;
      }

      const nextProfile = await resolveProfileRole(session.user);
      if (!canAccessStaffPortal(nextProfile.role)) {
        await supabase.auth.signOut();
        setProfile(null);
        setError("Access denied. Staff or admin role is required.");
        return;
      }

      setProfile(nextProfile);
      await loadApkMeta();
    } catch (sessionError) {
      setProfile(null);
      setError(sessionError.message || "Unable to check your session.");
    } finally {
      setLoading(false);
    }
  }, [loadApkMeta]);

  useEffect(() => {
    hydrateSession();
  }, [hydrateSession]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setSigningIn(true);
    setError("");

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (signInError) {
        throw new Error(signInError.message || "Invalid email or password.");
      }

      await hydrateSession();
    } catch (loginError) {
      setError(loginError.message || "Login failed. Please try again.");
    } finally {
      setSigningIn(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError("");
    try {
      const { data, error: signedUrlError } = await supabase.storage
        .from(SCANNER_RELEASES_BUCKET)
        .createSignedUrl(SCANNER_APK_OBJECT, 60);

      if (signedUrlError || !data?.signedUrl) {
        throw new Error(
          "Scanner APK is not available yet. Ask an administrator to upload echojham-scanner.apk."
        );
      }

      window.location.assign(data.signedUrl);
    } catch (downloadError) {
      setError(downloadError.message || "Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setEmail("");
    setPassword("");
    setError("");
  };

  return (
    <div className="min-h-screen flex font-sans bg-[#f3f2f1]">
      <div className="hidden md:flex w-1/2 bg-black items-center justify-center">
        <img
          src="/assets/echojham.logo3.png"
          alt="Echo Jham"
          className="w-64 mb-3"
          onError={(event) => {
            event.target.src = "https://via.placeholder.com/250?text=Echo+Jham";
          }}
        />
      </div>

      <div className="w-full md:w-1/2 bg-[#f3f2f1] flex items-center justify-center py-10">
        <div className="w-full max-w-sm text-center px-8">
          <h2 className="text-xl font-bold text-[#7b0000] mb-2 tracking-widest uppercase italic">
            Staff Scanner
          </h2>
          <p className="mb-6 text-xs text-gray-500">
            Download the EchoJham QR scanner. This page is not the admin portal.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs font-bold rounded-r-xl text-left">
              {error}
            </div>
          )}

          {loading ? (
            <p className="text-sm text-gray-500">Checking session...</p>
          ) : profile ? (
            <div className="space-y-5 text-left">
              <p className="text-sm text-gray-700">
                Signed in as <span className="font-semibold">{profile.fullName}</span>
              </p>
              <p className="text-xs text-gray-500">
                Latest APK: {formatUpdatedAt(apkUpdatedAt)}
              </p>
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className={`w-full py-3 rounded-md text-sm font-bold uppercase tracking-widest text-white ${
                  downloading ? "bg-gray-400" : "bg-[#7b0000] hover:bg-red-800"
                }`}
              >
                {downloading ? "Preparing download..." : "Download EchoJham Scanner"}
              </button>
              <ol className="text-left text-xs text-gray-600 space-y-2 list-decimal list-inside">
                <li>Open the downloaded APK in Chrome.</li>
                <li>Allow Install unknown apps for Chrome if Android asks.</li>
                <li>Open EchoJham Scanner and sign in with this same account.</li>
              </ol>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full py-2 text-xs font-semibold uppercase tracking-widest text-gray-500 hover:text-[#7b0000]"
              >
                Log out
              </button>
            </div>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="email"
                placeholder="Staff email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full px-4 py-3 rounded-md border border-gray-300 text-black text-sm focus:outline-none focus:ring-2 focus:ring-[#7b0000] bg-white"
                required
              />
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full px-4 pr-12 py-3 rounded-md border border-gray-300 text-black text-sm focus:outline-none focus:ring-2 focus:ring-[#7b0000] bg-white"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#7b0000] p-1 min-h-10 min-w-10 flex items-center justify-center"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <button
                type="submit"
                disabled={signingIn}
                className={`w-full sm:w-40 mx-auto py-3 rounded-md text-sm font-bold transition shadow-lg uppercase tracking-widest text-white ${
                  signingIn ? "bg-gray-400" : "bg-[#7b0000] hover:bg-red-800"
                }`}
              >
                {signingIn ? "Verifying..." : "Login"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffScanner;
